import React, { useState, useEffect } from "react";
import { getDatabase, ref, get } from "firebase/database";
import { app } from "./firebase/config";
import { POST_LENGTHS } from "./constants/postSettings";
import {
  FaArrowLeft,
  FaPlay,
  FaPause,
  FaClock,
  FaCalendarAlt,
  FaPalette,
  FaLanguage,
  FaImage,
  FaFacebook,
  FaWordpress,
  FaHashtag,
  FaSmile,
  FaUser,
  FaListUl,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaEdit,
  FaExclamationTriangle,
} from "react-icons/fa";

const AdminAutoPostConfigs = ({ userId, userName, onBack }) => {
  const [autoPostConfigs, setAutoPostConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(null);

  useEffect(() => {
    fetchAutoPostConfigs();
  }, [userId]);

  const fetchAutoPostConfigs = async () => {
    if (!userId) return;

    setLoading(true);
    try {
      const db = getDatabase(app);
      const configsRef = ref(db, `/posts/${userId}/`);
      const snapshot = await get(configsRef);

      if (snapshot.exists()) {
        const configList = [];
        snapshot.forEach((childSnapshot) => {
          configList.push({ id: childSnapshot.key, ...childSnapshot.val() });
        });

        // Sắp xếp theo thời gian đăng bài
        configList.sort((a, b) => {
          const timeA = a.time
            ? new Date(`1970-01-01T${a.time}:00Z`)
            : new Date();
          const timeB = b.time
            ? new Date(`1970-01-01T${b.time}:00Z`)
            : new Date();
          return timeA - timeB;
        });

        setAutoPostConfigs(configList);
      } else {
        setAutoPostConfigs([]);
      }
    } catch (error) {
      console.error("Lỗi khi tải thiết lập đăng bài tự động:", error);
      setAutoPostConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const getPostLengthLabel = (postLength) => {
    const foundLength = POST_LENGTHS.find(
      (length) => length.value === postLength
    );
    return foundLength ? foundLength.label : "Chưa thiết lập";
  };

  const getTopicStats = (topics) => {
    if (!topics) return { total: 0, posted: 0, pending: 0 };

    const topicArray = Object.values(topics);
    const total = topicArray.length;
    const posted = topicArray.filter(
      (topic) => topic.postStatus === "posted"
    ).length;
    const pending = topicArray.filter(
      (topic) => topic.postStatus === "not_started"
    ).length;

    return { total, posted, pending };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "posted":
        return "bg-green-100 text-green-800";
      case "not_started":
        return "bg-gray-100 text-gray-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "posted":
        return <FaCheckCircle className="text-green-600" />;
      case "not_started":
        return <FaTimesCircle className="text-gray-400" />;
      case "pending":
        return <FaSpinner className="text-yellow-600" />;
      default:
        return <FaTimesCircle className="text-gray-400" />;
    }
  };

  if (selectedConfig) {
    const topicStats = getTopicStats(selectedConfig.topics);

    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <button
              onClick={() => setSelectedConfig(null)}
              className="mr-4 p-2 bg-white rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <FaArrowLeft className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Chi tiết thiết lập đăng bài
              </h1>
              <p className="text-gray-600">
                {userName} • ID: {selectedConfig.id}
              </p>
            </div>
          </div>

          {/* Status Banner */}
          <div
            className={`p-4 rounded-lg border-l-4 ${
              selectedConfig.autoPostEnabled
                ? "bg-green-50 border-green-500"
                : "bg-red-50 border-red-500"
            }`}
          >
            <div className="flex items-center">
              {selectedConfig.autoPostEnabled ? (
                <FaPlay className="text-green-600 mr-2" />
              ) : (
                <FaPause className="text-red-600 mr-2" />
              )}
              <span
                className={`font-medium ${
                  selectedConfig.autoPostEnabled
                    ? "text-green-800"
                    : "text-red-800"
                }`}
              >
                {selectedConfig.autoPostEnabled
                  ? "Thiết lập đang hoạt động"
                  : "Thiết lập đã tạm dừng"}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left Column - Configuration Details */}
          <div className="xl:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FaEdit className="mr-2 text-blue-600" />
                Thông tin cơ bản
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Platform
                  </label>
                  <div className="flex items-center">
                    {selectedConfig.platform === "wordpress" ? (
                      <FaWordpress className="text-blue-600 mr-2" />
                    ) : (
                      <FaFacebook className="text-blue-600 mr-2" />
                    )}
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        selectedConfig.platform === "wordpress"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-purple-100 text-purple-800"
                      }`}
                    >
                      {selectedConfig.platform === "wordpress"
                        ? "WordPress"
                        : "Facebook"}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Loại nội dung
                  </label>
                  <p className="text-gray-800">
                    {selectedConfig.contentType || "Chưa xác định"}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Loại dự án
                  </label>
                  <p className="text-gray-800">
                    {selectedConfig.projectType || "Chưa xác định"}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Độ dài bài viết
                  </label>
                  <div className="flex items-center">
                    <FaListUl className="text-gray-500 mr-2" />
                    <span className="text-gray-800">
                      {getPostLengthLabel(selectedConfig.postLength)}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phong cách viết
                  </label>
                  <div className="flex items-center">
                    <FaPalette className="text-gray-500 mr-2" />
                    <span className="text-gray-800">
                      {selectedConfig.postStyles || "Chưa chọn"}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Số lượng chủ đề
                  </label>
                  <span className="text-gray-800">
                    {selectedConfig.topicCount || 0}
                  </span>
                </div>
              </div>

              {/* Prompt */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prompt chính
                </label>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 leading-relaxed">
                    {selectedConfig.prompt || "Chưa có prompt"}
                  </p>
                </div>
              </div>
            </div>

            {/* Schedule Settings */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FaClock className="mr-2 text-green-600" />
                Lịch trình đăng bài
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Thời gian */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thời gian đăng
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {Array.isArray(selectedConfig.times) &&
                    selectedConfig.times.length > 0
                      ? selectedConfig.times.map((time, idx) => (
                          <span
                            key={idx}
                            className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {time}
                          </span>
                        ))
                      : selectedConfig.time && (
                          <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                            {selectedConfig.time}
                          </span>
                        )}
                  </div>
                </div>

                {/* Ngày trong tuần */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ngày đăng bài
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {selectedConfig.selectedDays &&
                      Object.entries(selectedConfig.selectedDays)
                        .filter(([_, isSelected]) => isSelected)
                        .map(([day]) => {
                          const dayMap = {
                            monday: "Thứ 2",
                            tuesday: "Thứ 3",
                            wednesday: "Thứ 4",
                            thursday: "Thứ 5",
                            friday: "Thứ 6",
                            saturday: "Thứ 7",
                            sunday: "Chủ nhật",
                          };
                          return (
                            <span
                              key={day}
                              className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium"
                            >
                              {dayMap[day]}
                            </span>
                          );
                        })}
                  </div>
                </div>
              </div>
            </div>

            {/* Platform Specific Settings */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FaUser className="mr-2 text-purple-600" />
                Cài đặt platform
              </h3>

              {selectedConfig.platform === "facebook" ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fanpage
                    </label>
                    <p className="text-gray-800">
                      {selectedConfig.fanpageName || "Chưa chọn"}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fanpage ID
                    </label>
                    <p className="text-gray-600 font-mono text-sm">
                      {selectedConfig.fanpageID || "Chưa có"}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website
                    </label>
                    <p className="text-blue-600 break-all">
                      {selectedConfig.wordpressSiteName ||
                        selectedConfig.websiteUrl ||
                        "Chưa có"}
                    </p>
                  </div>

                  {selectedConfig.wordpressUsername && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Username
                      </label>
                      <p className="text-gray-800">
                        {selectedConfig.wordpressUsername}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Advanced Settings */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FaExclamationTriangle className="mr-2 text-orange-600" />
                Cài đặt nâng cao
              </h3>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <FaImage className="text-gray-500 mr-1" />
                  </div>
                  <span className="block text-sm text-gray-500 mb-1">
                    Cho phép không có ảnh
                  </span>
                  <span
                    className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      selectedConfig.allowNoImage
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {selectedConfig.allowNoImage ? "Có" : "Không"}
                  </span>
                </div>

                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <FaHashtag className="text-gray-500 mr-1" />
                  </div>
                  <span className="block text-sm text-gray-500 mb-1">
                    Bao gồm thông tin liên hệ
                  </span>
                  <span
                    className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      selectedConfig.includeContactInfo
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {selectedConfig.includeContactInfo ? "Có" : "Không"}
                  </span>
                </div>

                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <FaUser className="text-gray-500 mr-1" />
                  </div>
                  <span className="block text-sm text-gray-500 mb-1">
                    Sử dụng thông tin Fanpage
                  </span>
                  <span
                    className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      selectedConfig.useFanpageInfo
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {selectedConfig.useFanpageInfo ? "Có" : "Không"}
                  </span>
                </div>

                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <FaEdit className="text-gray-500 mr-1" />
                  </div>
                  <span className="block text-sm text-gray-500 mb-1">
                    Loại input
                  </span>
                  <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    {selectedConfig.inputType || "Chưa xác định"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Topics List */}
          <div className="space-y-6">
            {/* Topics Statistics */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FaListUl className="mr-2 text-indigo-600" />
                Thống kê chủ đề
              </h3>

              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-700">Tổng số chủ đề</span>
                  <span className="font-bold text-gray-800">
                    {topicStats.total}
                  </span>
                </div>

                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <span className="text-green-700">Đã đăng</span>
                  <span className="font-bold text-green-800">
                    {topicStats.posted}
                  </span>
                </div>

                <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                  <span className="text-yellow-700">Chưa đăng</span>
                  <span className="font-bold text-yellow-800">
                    {topicStats.pending}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Tiến độ đăng bài</span>
                    <span>
                      {topicStats.total > 0
                        ? Math.round(
                            (topicStats.posted / topicStats.total) * 100
                          )
                        : 0}
                      %
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${
                          topicStats.total > 0
                            ? (topicStats.posted / topicStats.total) * 100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Topics List */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FaListUl className="mr-2 text-indigo-600" />
                  Danh sách chủ đề
                </h3>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {selectedConfig.topics &&
                Object.values(selectedConfig.topics).length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {Object.values(selectedConfig.topics).map(
                      (topic, index) => (
                        <div
                          key={topic.id || index}
                          className="p-4 hover:bg-gray-50"
                        >
                          <div className="flex items-start space-x-3">
                            <div className="mt-1">
                              {getStatusIcon(topic.postStatus)}
                            </div>

                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 leading-5">
                                {topic.title}
                              </p>

                              <div className="mt-2 flex items-center space-x-2">
                                <span
                                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                    topic.postStatus
                                  )}`}
                                >
                                  {topic.postStatus === "posted"
                                    ? "Đã đăng"
                                    : topic.postStatus === "pending"
                                    ? "Đang chờ"
                                    : "Chưa đăng"}
                                </span>

                                {topic.status && (
                                  <span
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                      topic.status
                                    )}`}
                                  >
                                    {topic.status === "pending"
                                      ? "Chờ xử lý"
                                      : topic.status}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <div className="p-6 text-center text-gray-500">
                    Chưa có chủ đề nào được tạo
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center mb-4">
          <button
            onClick={onBack}
            className="mr-4 p-2 bg-white rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <FaArrowLeft className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Thiết lập đăng bài tự động
            </h1>
            <p className="text-gray-600">
              Quản lý thiết lập đăng bài tự động của {userName}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-gray-600">
              Đang tải dữ liệu thiết lập đăng bài...
            </p>
          </div>
        ) : autoPostConfigs.length === 0 ? (
          <div className="text-center py-12">
            <FaExclamationTriangle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Chưa có thiết lập nào
            </h3>
            <p className="text-gray-500">
              Người dùng chưa tạo thiết lập đăng bài tự động nào.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">
                Danh sách thiết lập ({autoPostConfigs.length})
              </h3>
            </div>

            <div className="divide-y divide-gray-200">
              {autoPostConfigs.map((item, index) => {
                const topicStats = getTopicStats(item.topics);

                return (
                  <div
                    key={item.id}
                    className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setSelectedConfig(item)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <h4 className="text-lg font-medium text-gray-800 mr-3">
                            {item.prompt || `Thiết lập #${index + 1}`}
                          </h4>
                          <span
                            className={`px-3 py-1 rounded-full text-sm font-medium ${
                              item.autoPostEnabled
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {item.autoPostEnabled
                              ? "🔵 Đang chạy"
                              : "⚪ Đã tắt"}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                          <div className="flex items-center text-sm text-gray-600">
                            {item.platform === "wordpress" ? (
                              <FaWordpress className="mr-2 text-blue-600" />
                            ) : (
                              <FaFacebook className="mr-2 text-blue-600" />
                            )}
                            <span>
                              {item.platform === "wordpress"
                                ? "WordPress"
                                : "Facebook"}
                            </span>
                          </div>

                          <div className="flex items-center text-sm text-gray-600">
                            <FaClock className="mr-2 text-gray-500" />
                            <span>
                              {Array.isArray(item.times) &&
                              item.times.length > 0
                                ? item.times.join(", ")
                                : item.time || "Chưa đặt"}
                            </span>
                          </div>

                          <div className="flex items-center text-sm text-gray-600">
                            <FaListUl className="mr-2 text-gray-500" />
                            <span>{topicStats.total} chủ đề</span>
                          </div>

                          <div className="flex items-center text-sm text-gray-600">
                            <FaCheckCircle className="mr-2 text-green-500" />
                            <span>{topicStats.posted} đã đăng</span>
                          </div>
                        </div>

                        {item.selectedDays && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {Object.entries(item.selectedDays)
                              .filter(([_, isSelected]) => isSelected)
                              .map(([day]) => {
                                const dayMap = {
                                  monday: "T2",
                                  tuesday: "T3",
                                  wednesday: "T4",
                                  thursday: "T5",
                                  friday: "T6",
                                  saturday: "T7",
                                  sunday: "CN",
                                };
                                return (
                                  <span
                                    key={day}
                                    className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs"
                                  >
                                    {dayMap[day]}
                                  </span>
                                );
                              })}
                          </div>
                        )}
                      </div>

                      <div className="ml-4">
                        <div className="text-right">
                          <p className="text-sm text-gray-500">ID: {item.id}</p>
                          {topicStats.total > 0 && (
                            <div className="mt-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-green-600 h-2 rounded-full"
                                  style={{
                                    width: `${
                                      (topicStats.posted / topicStats.total) *
                                      100
                                    }%`,
                                  }}
                                ></div>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {Math.round(
                                  (topicStats.posted / topicStats.total) * 100
                                )}
                                %
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAutoPostConfigs;
